import { Link, useRouteLoaderData } from "@remix-run/react";

import { HELP_CATEGORIES } from "~/components/help-center/categories";
import { HelpSearchTrigger } from "~/components/help-center/help-search";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "~/components/ui/accordion";

import type { HelpArticleNavItem } from "~/routes/help";

// AIDEV-NOTE: Help center landing page. Rebuilt on the design-system Accordion
// rather than hand-rolled Collapsible sections, so keyboard behaviour, focus
// rings and the open/close chevron come from the shared primitive instead of
// being re-implemented here. Layout is a single column supplied by the parent
// route — this file owns no width, padding or breakpoint of its own, which is
// what let the old version overflow on mobile.
export default function HelpIndexPage() {
    const helpData = useRouteLoaderData("routes/help") as
        | { articles: HelpArticleNavItem[] }
        | undefined;
    const articles = helpData?.articles ?? [];

    const grouped: Record<string, HelpArticleNavItem[]> = {};
    for (const article of articles) {
        if (!grouped[article.category]) {
            grouped[article.category] = [];
        }
        grouped[article.category].push(article);
    }

    const populated = HELP_CATEGORIES.filter(
        (category) => grouped[category.value]?.length,
    );

    return (
        <div className="py-6">
            <p className="text-sm leading-relaxed text-brand-forest-700/80">
                Browse our help articles to learn how to get the most out of
                Parts Companion.
            </p>

            <div className="mt-4">
                <HelpSearchTrigger />
            </div>

            {populated.length === 0 ? (
                <p className="mt-8 text-sm text-muted-foreground">
                    Help articles are on their way.
                </p>
            ) : (
                <Accordion type="single" collapsible className="mt-6">
                    {populated.map((category) => {
                        const categoryArticles = grouped[category.value] ?? [];
                        const Icon = category.icon;

                        return (
                            <AccordionItem
                                key={category.value}
                                value={category.value}
                            >
                                <AccordionTrigger>
                                    <span className="flex min-w-0 items-start gap-3">
                                        <Icon className="mt-0.5 size-4 shrink-0 text-brand-forest-400" />
                                        <span className="min-w-0">
                                            <span className="block font-semibold text-brand-forest-800">
                                                {category.label}
                                            </span>
                                            {/* AIDEV-NOTE: wraps rather than truncating — the
                                                previous version clipped these mid-sentence on
                                                narrow screens. */}
                                            <span className="mt-0.5 block text-xs font-normal leading-snug text-brand-forest-600/70">
                                                {category.description}
                                            </span>
                                        </span>
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ul className="space-y-0.5">
                                        {categoryArticles.map((article) => (
                                            <li key={article.slug}>
                                                <Link
                                                    to={`/help/${article.category}/${article.slug}`}
                                                    className="block rounded-lg px-2 py-2 text-sm text-brand-forest-700 transition-colors hover:bg-brand-forest-50 hover:text-brand-forest-900"
                                                >
                                                    {article.title}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            )}
        </div>
    );
}
